import React, { useState, useEffect } from 'react';
import {
  CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Database, Cloud, Monitor, Activity, Bell
} from 'lucide-react';
import { healthMonitorService } from '../services/healthMonitorService';
import {
  SystemStatus, ServiceHealth, DatabaseHealth, ExternalAPIHealth,
  SystemMetrics, Alert as HealthAlert,
} from '../types/healthMonitor';

const tabs = ['Services', 'Databases', 'External APIs', 'Metrics', 'All Alerts'];
const tabIcons = [Monitor, Database, Cloud, Activity, Bell];

function StatusIcon({ status, size = 'h-5 w-5' }: { status: string; size?: string }) {
  switch (status) {
    case 'HEALTHY': return <CheckCircle className={`${size} text-green-500`} />;
    case 'DEGRADED': return <AlertTriangle className={`${size} text-yellow-500`} />;
    case 'UNHEALTHY': return <XCircle className={`${size} text-red-500`} />;
    default: return <AlertTriangle className={`${size} text-gray-400`} />;
  }
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    HEALTHY: 'bg-green-100 text-green-800', DEGRADED: 'bg-yellow-100 text-yellow-800',
    UNHEALTHY: 'bg-red-100 text-red-800', ACTIVE: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
}

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    CRITICAL: 'bg-red-100 text-red-800 border-red-200', WARNING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    INFO: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colors[severity] || 'bg-gray-100 text-gray-800'}`}>{severity}</span>;
}

function ProgressBar({ value, danger }: { value: number; danger?: boolean }) {
  const color = danger ? 'bg-red-500' : 'bg-blue-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 rounded-full h-2"><div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${Math.min(value, 100)}%` }} /></div>
      <span className="text-sm text-gray-500 w-14 text-right">{value.toFixed(1)}%</span>
    </div>
  );
}

const HealthMonitoringPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [databases, setDatabases] = useState<DatabaseHealth[]>([]);
  const [externalAPIs, setExternalAPIs] = useState<ExternalAPIHealth[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [alerts, setAlerts] = useState<HealthAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      const [statusData, servicesData, dbData, apisData, metricsData, alertsData] = await Promise.all([
        healthMonitorService.getSystemStatus(), healthMonitorService.getServicesHealth(),
        healthMonitorService.getDatabasesHealth(), healthMonitorService.getExternalAPIsHealth(),
        healthMonitorService.getSystemMetrics(), healthMonitorService.getActiveAlerts(),
      ]);
      setSystemStatus(statusData); setServices(servicesData.services);
      setDatabases(dbData.databases); setExternalAPIs(apisData.external_apis);
      setSystemMetrics(metricsData); setAlerts(alertsData.alerts);
    } catch (error) { console.error('Failed to fetch health data:', error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); if (autoRefresh) { const i = setInterval(fetchData, 30000); return () => clearInterval(i); } }, [autoRefresh]);
  const handleRefresh = () => { setLoading(true); fetchData(); };
  const handleAcknowledge = async (id: string) => { try { await healthMonitorService.acknowledgeAlert(id); fetchData(); } catch (e) { console.error(e); } };
  const handleResolve = async (id: string) => { try { await healthMonitorService.resolveAlert(id); fetchData(); } catch (e) { console.error(e); } };

  if (loading && !systemStatus) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">System Health Monitoring</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setAutoRefresh(!autoRefresh)} className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50">
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button onClick={handleRefresh} disabled={loading} className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50">
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {systemStatus && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-2"><StatusIcon status={systemStatus.status} /><span className="font-semibold">System Status</span></div>
            <StatusBadge status={systemStatus.status} />
          </div>
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-1">Services</h3>
            <p className="text-3xl font-bold">{systemStatus.services_healthy}/{systemStatus.services_total}</p>
            <p className="text-sm text-gray-500">Healthy</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-1">Active Alerts</h3>
            <p className={`text-3xl font-bold ${systemStatus.active_alerts > 0 ? 'text-red-600' : 'text-green-600'}`}>{systemStatus.active_alerts}</p>
            <p className="text-sm text-gray-500">{systemStatus.critical_alerts} Critical</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-1">Last Updated</h3>
            <p>{new Date(systemStatus.timestamp).toLocaleTimeString()}</p>
            <p className="text-sm text-gray-500">{new Date(systemStatus.timestamp).toLocaleDateString()}</p>
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-2">Active Alerts</h2>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border ${alert.severity === 'CRITICAL' ? 'bg-red-50 border-red-200' : alert.severity === 'WARNING' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{alert.type} - {alert.component}</p>
                    <p className="text-sm mt-1">{alert.message}</p>
                    {alert.details && <p className="text-sm text-gray-600 mt-1">{alert.details}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleAcknowledge(alert.id)} disabled={alert.status !== 'ACTIVE'} className="text-sm px-2 py-1 border rounded hover:bg-white disabled:opacity-50">Acknowledge</button>
                    <button onClick={() => handleResolve(alert.id)} className="text-sm px-2 py-1 border rounded hover:bg-white">Resolve</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border">
        <div className="flex border-b overflow-x-auto">
          {tabs.map((tab, i) => {
            const Icon = tabIcons[i];
            return (
              <button key={tab} onClick={() => setTabValue(i)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${tabValue === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <Icon className="h-4 w-4" />{tab}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tabValue === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s) => (
                <div key={s.service_name} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">{s.service_name}</h3><StatusIcon status={s.status} /></div>
                  <StatusBadge status={s.status} />
                  <hr className="my-2" />
                  <p className="text-sm text-gray-500">Response Time: {s.response_time_ms}ms</p>
                  <p className="text-sm text-gray-500">Uptime: {s.uptime_percentage.toFixed(2)}%</p>
                  <p className="text-sm text-gray-500">Consecutive Fails: {s.consecutive_fails}</p>
                  <p className="text-sm text-gray-500">Last Checked: {new Date(s.last_checked).toLocaleTimeString()}</p>
                  {s.error_message && <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{s.error_message}</div>}
                </div>
              ))}
            </div>
          )}

          {tabValue === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {databases.map((db) => (
                <div key={db.database_name} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">{db.database_name}</h3><StatusIcon status={db.status} /></div>
                  <StatusBadge status={db.status} />
                  <hr className="my-2" />
                  <p className="text-sm text-gray-500">Response Time: {db.response_time_ms}ms</p>
                  <p className="text-sm text-gray-500">Last Checked: {new Date(db.last_checked).toLocaleTimeString()}</p>
                  {db.connection_pool && (
                    <div className="mt-2"><p className="text-sm font-medium">Connection Pool</p><p className="text-sm text-gray-500">Active: {db.connection_pool.active_connections} / {db.connection_pool.max_connections}</p><p className="text-sm text-gray-500">Idle: {db.connection_pool.idle_connections}</p></div>
                  )}
                  {db.error_message && <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{db.error_message}</div>}
                </div>
              ))}
            </div>
          )}

          {tabValue === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {externalAPIs.map((api) => (
                <div key={api.api_name} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">{api.api_name}</h3><StatusIcon status={api.status} /></div>
                  <StatusBadge status={api.status} />
                  <hr className="my-2" />
                  <p className="text-sm text-gray-500">Response Time: {api.response_time_ms}ms</p>
                  <p className="text-sm text-gray-500">Last Checked: {new Date(api.last_checked).toLocaleTimeString()}</p>
                  {api.rate_limit_remaining !== undefined && <p className="text-sm text-gray-500">Rate Limit Remaining: {api.rate_limit_remaining}</p>}
                  {api.error_message && <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{api.error_message}</div>}
                </div>
              ))}
            </div>
          )}

          {tabValue === 3 && systemMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4"><h3 className="font-semibold mb-2">CPU Usage</h3><ProgressBar value={systemMetrics.cpu_usage_percent} danger={systemMetrics.cpu_usage_percent > 80} /></div>
              <div className="border rounded-lg p-4"><h3 className="font-semibold mb-2">Memory Usage</h3><ProgressBar value={systemMetrics.memory_usage_percent} danger={systemMetrics.memory_usage_percent > 80} /></div>
              <div className="border rounded-lg p-4"><h3 className="font-semibold mb-2">Disk Usage</h3><ProgressBar value={systemMetrics.disk_usage_percent} danger={systemMetrics.disk_usage_percent > 80} /></div>
              <div className="border rounded-lg p-4"><h3 className="font-semibold mb-2">Network</h3><p className="text-sm text-gray-500">In: {(systemMetrics.network_in_bytes / 1024 / 1024).toFixed(2)} MB</p><p className="text-sm text-gray-500">Out: {(systemMetrics.network_out_bytes / 1024 / 1024).toFixed(2)} MB</p></div>
              <div className="border rounded-lg p-4"><h3 className="font-semibold mb-2">Requests</h3><p className="text-sm text-gray-500">Active: {systemMetrics.active_requests}</p><p className="text-sm text-gray-500">Per Minute: {systemMetrics.requests_per_minute}</p></div>
            </div>
          )}

          {tabValue === 4 && (
            <div className="divide-y">
              {alerts.map((alert) => (
                <div key={alert.id} className="py-3 flex items-start gap-3">
                  {alert.severity === 'CRITICAL' ? <XCircle className="h-5 w-5 text-red-500 mt-0.5" /> : alert.severity === 'WARNING' ? <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" /> : <Bell className="h-5 w-5 text-blue-500 mt-0.5" />}
                  <div className="flex-1">
                    <p className="font-medium">{alert.type} - {alert.component}</p>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                    <p className="text-xs text-gray-400 mt-1">Created: {new Date(alert.created_at).toLocaleString()}</p>
                    {alert.acknowledged_at && <p className="text-xs text-gray-400">Acknowledged: {new Date(alert.acknowledged_at).toLocaleString()}</p>}
                  </div>
                  <StatusBadge status={alert.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthMonitoringPage;
